import { RedRobinApiService } from '@api_patient/red_robin_api.service';
import { UnitsApiService } from '@api_patient/units_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { AuthService } from '@application/authentication/auth.service';
import { FeatureFlagService } from '@application/feature_flags/feature_flag.service';
import { HttpStatusCode } from '@application/http.service';
import { isVisibaErrorResponse } from '@application/http_error';
import { getAbsoluteRoute } from '@application/route';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { AsyncSignalValue } from '@component/async.component';
import { isProblemDetails } from '@cortex-shim/problem_details';
import { RouterService } from '@cortex-shim/router.service';
import { signal } from '@preact/signals-react';
import { Injectable, ControllerProtocol } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { NiN } from '@visiba/national-identity-number';

export enum RedRobinScreen {
	WhoIsRespondant = '',
	Triage = 'triage',
	Closed = 'closed',
	Error = 'error',
}

type ClosedReason = 'OutsideMonitoredHours' | 'CapLimitReached' | 'Other';
interface FlowData {
	currentCountry: keyof NiN.Implementations;
	allowRepresentative: boolean;
	closedReason: ClosedReason | null;
}

@Injectable()
export class ViewRedRobinController implements ControllerProtocol {
	public flowData = signal<AsyncSignalValue<FlowData>>({
		value: null,
		flowState: 'pending',
	});

	private readonly routeBase: string;

	private readonly logger = new Logger('ViewRedRobinController');

	constructor(
		public readonly unitService: UnitService,
		public readonly routerService: RouterService,
		public readonly universalConfig: UniversalConfig,
		private readonly unitsApiService: UnitsApiService,
		private readonly redRobinApiService: RedRobinApiService,
		private readonly featureFlagService: FeatureFlagService,
		private readonly authService: AuthService,

		private readonly analyticService: AnalyticService,
	) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
		this.routeBase = unit.routeWithoutLicenceholder;
	}

	async whenMount() {
		this.analyticService.trackPageView({ customTitle: 'Red Robin' });

		this.logger.logInformation('Mounting ViewRedRobinController');

		if (!this.featureFlagService.checkFlag('RedRobin.UseControlled')) {
			this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.NotAllowedToBeHere, 'No feature flag');
			this.goToReceptionHome();

			return;
		}

		const unitConfigResult = await this.unitsApiService.getUnitConfig(this.universalConfig.selectedUnitId);
		if (unitConfigResult.err) return Err(unitConfigResult.err);

		const allowRepresentative = unitConfigResult.ok.data.Offer?.AllowClientAgents || false;
		const userNationality = this.authService.getUser()?.nationality;
		const currentCountry =
			userNationality && NiN.supportsCountryCode(userNationality)
				? (userNationality as keyof NiN.Implementations)
				: this.universalConfig.selectedCountryCode.peek();

		const openResult = await this.redRobinApiService.getOpenStatus();

		this.flowData.value = {
			value: {
				currentCountry,
				allowRepresentative,
				closedReason: null,
			},
			flowState: 'accessible',
		};

		if (openResult.err || openResult.ok.data.isOpen === false) {
			this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.Closed, 'Initial load');
			const closedReason = (openResult.ok?.data.closedReason ?? 'Other') as ClosedReason;
			this.goToScreen(RedRobinScreen.Closed, { replace: true, closedReason });

			return;
		}
	}

	public goToReceptionHome() {
		this.routerService.navigateByUrl(getAbsoluteRoute(this.routeBase));

		if (
			(window as typeof window & { __TEMP__overrideNavigation: Record<string, unknown> | undefined })['__TEMP__overrideNavigation'] !=
			null
		) {
			window.location.reload();
		}
	}

	public goToScreen(
		screen: RedRobinScreen,
		options: {
			replace?: boolean;
			sessionId?: string;
			closedReason?: ClosedReason;
		} = {},
	): void {
		let path = '';
		const analyticActionName = screen;

		switch (screen) {
			case RedRobinScreen.WhoIsRespondant:
				path = '';
				break;
			case RedRobinScreen.Triage:
				path = `${options.sessionId}`;
				break;
			case RedRobinScreen.Error:
				path = 'error';
				break;
			case RedRobinScreen.Closed:
				path = 'closed';
				this.updateFlowData({ closedReason: options.closedReason ?? null });

				break;
		}

		this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.GoToScreen, analyticActionName);
		const url = [this.routeBase, 'triage', path].filter(Boolean);

		this.routerService.navigateByUrl(getAbsoluteRoute(url.join('/')), { replace: options.replace });
	}

	public getFlowData(): FlowData {
		if (this.flowData.value?.flowState === 'accessible') {
			return this.flowData.value.value;
		}

		throw new Error("FlowData isn't accessible yet. Make sure to run whenMount() before trying to access flowData.");
	}

	public updateFlowData(data: Partial<FlowData>): void {
		if (this.flowData.value.value === null) {
			return;
		}

		this.flowData.value = {
			value: {
				...this.flowData.value.value,
				...data,
			},
			flowState: 'accessible',
		};
	}

	public async createSesssion(userIsPatient: boolean, patientNin: Nullable<string>): Promise<void> {
		const result = await this.redRobinApiService.createSession(userIsPatient, patientNin);
		if (result.err) {
			const error = result.err;
			const errorData = JSON.parse(typeof error.data === 'string' ? error.data : '{}');

			if (
				error.statusCode === HttpStatusCode.BadRequest_400 &&
				isProblemDetails(errorData) &&
				errorData.title === 'Service is closed'
			) {
				this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.Closed, 'Session create');
				const closedReason = errorData.detail as ClosedReason;
				this.goToScreen(RedRobinScreen.Closed, { replace: true, closedReason });

				return;
			}

			this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.ErrorCreatingSession);

			this.flowData.value = {
				value: null,
				flowState: 'error',
			};

			return;
		}
		this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.SessionCreated);

		this.goToScreen(RedRobinScreen.Triage, { replace: true, sessionId: result.ok.data.split('/').pop() });
	}
}

export enum ViewRedRobinAnalyticActions {
	GoToScreen = 'Go-to-screen',
	Closed = 'Closed through demand management',
	NotAllowedToBeHere = 'Not allowed to be here',
	DoesNotAllowRepresentative = 'Does not allow representative',
	PatientUnderOneYearOfAge = 'The patient is under one year of age',
	FormIsInvalid = 'Form is invalid',
	SessionCreated = 'Session sucessfully created',
	ErrorCreatingSession = 'Error while creating session',
}
